import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Search } from "lucide-react";

export default function SearchableDropdown({
  options = [],
  value = "",
  onChange,
  placeholder = "Select...",
  name = "",
  className = ""
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Reset search term when dropdown is opened/closed
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  const filteredOptions = options.filter((option) => {
    const text = typeof option === "string" ? option : option.name || "";
    return text.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleSelect = (optionValue) => {
    onChange({
      target: {
        name,
        value: optionValue
      }
    });
    setIsOpen(false);
  };

  const displayValue = typeof value === "string" ? value : value?.name || "";

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`border-2 border-slate-200 rounded-xl w-full text-sm font-medium bg-white text-left flex justify-between items-center transition-all duration-300 outline-none
          ${className.includes("p-") ? "" : "px-4 py-3"}
          ${isOpen ? "border-[#0b9949] ring-4 ring-green-50" : "hover:border-slate-300"} ${className}`}
      >
        <span className={displayValue ? "text-slate-800" : "text-slate-400"}>
          {displayValue || placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`text-slate-400 transition-transform duration-300 ${isOpen ? "rotate-180 text-[#0b9949]" : ""}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-[100] mt-2 w-full bg-white rounded-2xl border border-slate-100 shadow-2xl p-3 flex flex-col gap-2 max-h-[300px]">
          {/* Search Box */}
          <div className="relative flex items-center shrink-0">
            <Search className="absolute left-3.5 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-2 border-slate-100 focus:border-[#0b9949] rounded-xl pl-10 pr-4 py-2.5 w-full text-sm font-medium bg-slate-50/50 outline-none transition-all"
              autoFocus
            />
          </div>

          {/* Options List */}
          <div className="overflow-y-auto flex-1 pr-1 space-y-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const optVal = typeof option === "string" ? option : option.name || "";
                const isSelected = displayValue === optVal;

                return (
                  <button
                    key={typeof option === "string" ? option : option.id || optVal}
                    type="button"
                    onClick={() => handleSelect(optVal)}
                    className={`w-full text-left px-3 py-2.5 text-sm rounded-xl font-medium transition-colors outline-none
                      ${isSelected 
                        ? "bg-emerald-50/50 text-[#0b9949] font-bold" 
                        : "text-slate-700 hover:bg-slate-50"}`}
                  >
                    {optVal}
                  </button>
                );
              })
            ) : (
              <div className="text-center py-4 text-xs font-semibold text-slate-400">
                No matching options
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
